import { List } from 'immutable';
import PropTypes from 'prop-types';
// CORE
import React, { useState, useMemo, useCallback } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { transactionListSelectors } from 'app-components/flux/transaction-list';

import { rootUiActions } from 'app-components/flux/root-ui';
// Data
import { useDispatch } from 'react-redux';

import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
// MUI
import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import SearchBox from 'components/SearchBox';
import AccountMultiSelect from 'components/SimplifiSelects/AccountMultiSelect';
import TransactionSelectList from 'components/TransactionSelectList';
// Custom
import useTxnActions from 'data/transactionActions/useTxnActions';
import { DIALOG_TYPE } from './actions';

const useStyles = makeStyles()((theme) => ({
  dialogRoot: {
    paddingBottom: '0px !important',
    display: 'flex',
    flexDirection: 'column',
  },
  searchRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 20,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: 312,
  },
  searchBar: {
    height: 40,
    paddingLeft: 4,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    margin: '0 12px 0 auto',
    '&.open': {
      width: 242,
    },
    '&:hover': {
      bordercolor: theme.palette.text.primary,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    fontSize: '14px',
    lineHeight: 1.43,
    fontWeight: 'normal',
    color: theme.palette.text.primary,
    '&:-webkit-autofill': {
      // disable autofill color because it makes it ugly
      '-webkit-background-clip': 'text',
    },
  },
  textColor: {
    color: theme.palette.text.secondary,
  },
}));

const MatchTxnDialog = (props) => {
  const { classes } = useStyles();
  const { txn = {}, transactions, wrapperId = '' } = props;
  const dispatch = useDispatch();

  const [open, setOpen] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState();
  const [search, setSearch] = useState();
  const [selectedAccounts, setSelectedAccounts] = useState(() => [
    accountsSelectors.getAccountById(undefined, txn?.accountId),
  ]); // func so only evaluates once

  const { matchTransactions } = useTxnActions({ wrapperId });

  const onClose = useCallback(() => {
    setSearch(null);
    setSelectedTxn(null);
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE));
    setOpen(false);
  }, [dispatch]);

  const accountIdList = useMemo(() => selectedAccounts.map((acc) => acc?.id), [selectedAccounts]);

  // data stuff
  const matchOverdueTransaction = () => {
    matchTransactions([selectedTxn, txn]);
    onClose();
  };

  const selectTxn = (e) => {
    const clickedTxn = JSON.parse(e.currentTarget.id);
    if (selectedTxn && selectedTxn.id === clickedTxn.id) {
      setSelectedTxn(null);
    } else {
      setSelectedTxn(clickedTxn);
    }
  };

  const updateAccountSelection = (accounts) => {
    setSelectedAccounts(accounts);
  };

  const selectorProps = transactions
    ? // if transactions already provided, no need to feed selector props
      undefined
    : {
        matchTxn: txn,
        filter: search,
        accountIds: List(selectedAccounts.map((account) => account?.id)),
      };

  return (
    <StdDialog
      onClose={onClose}
      open={Boolean(open)}
      sharedcomponentid='MATCH_TXN_DIALOG'
      size='md'
      title='Link Transaction'
    >
      <DialogContent className={classes.dialogRoot}>
        <div className={classes.searchRow}>
          <div className={classes.stack}>
            <Typography variant='bodyRegular' className={classes.textColor} noWrap>
              Choose transaction to link to
            </Typography>

            <Typography variant='bodyMedium' noWrap sx={{ width: '100%' }}>
              {txn?.payee}
            </Typography>
          </div>
          <SearchBox
            initialValue={search}
            onSearch={(key) => setSearch(key && key.toLowerCase())}
            autoSearch
            autoFocus
            collapsable={false}
            placeholder='Search transactions'
            classes={{
              root: classes.searchBar,
              input: classes.searchInput,
            }}
          />

          {/* Account select thing here */}
          <AccountMultiSelect
            id='match-account-select'
            selectedItems={accountIdList}
            onSelected={updateAccountSelection}
          />
        </div>

        <TransactionSelectList
          clickFunc={selectTxn}
          highlight={selectedTxn?.id}
          filter={search}
          onReset={() => setSearch(null)}
          transactions={transactions}
          transactionListSelector={transactionListSelectors.getSortedTransactionsForAccountIds}
          selectorProps={selectorProps}
        />
      </DialogContent>

      <StdDialogActions
        primary={{
          children: 'Link',
          variant: 'contained',
          onClick: matchOverdueTransaction,
          disabled: !selectedTxn,
        }}
      />
    </StdDialog>
  );
};

MatchTxnDialog.propTypes = {
  txn: PropTypes.object,
  transactions: PropTypes.object,
  wrapperId: PropTypes.string,
};

export default MatchTxnDialog;
